import { computed } from 'vue';
import { useI18n } from 'vue-i18n/index';
import type {
  ExpertReviewAreaObject,
  ExpertReviewPropertyType,
} from '@/store/modules/Indices/ExpertReviewModule';

export const FILTERS = [
  { key: 'needs', label: 'expertReviewFilterNeedsReview', count: 'PENDING' },
  { key: 'high', label: 'expertReviewFilterHighRisk', count: 'high' },
  { key: 'approved', label: 'expertReviewFilterApproved', count: 'APPROVED' },
  { key: 'adjusted', label: 'expertReviewFilterAdjusted', count: 'ADJUSTED' },
  { key: 'rejected', label: 'expertReviewFilterRejected', count: 'REJECTED' },
  { key: 'all', label: 'expertReviewFilterAll', count: 'all' },
] as const;

const NUMBER_FORMAT = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
});

export const fmt = (value: number | null) =>
  value == null ? '—' : NUMBER_FORMAT.format(value);

export const pct = (value: number | null) =>
  value == null ? '—' : (value > 0 ? '+' : '') + value.toFixed(1) + '%';

export const deltaClass = (value: number | null) =>
  value == null || Math.abs(value) < 2
    ? 'text-gray-500'
    : value > 0
    ? 'text-success'
    : 'text-danger';

export const livePrice = (row: ExpertReviewAreaObject) =>
  row.status === 'PENDING' ? row.pricePerSqm : row.publishedPrice;

export const liveQoq = (row: ExpertReviewAreaObject) => {
  if (row.status === 'PENDING') return row.qoqChange;

  const published = row.publishedPrice;
  const previous = row.previousPricePerSqm;
  if (published == null || previous == null || previous === 0) return null;
  return ((published - previous) / previous) * 100;
};

export const propertyTypeKey = (type: ExpertReviewPropertyType) =>
  type === 'LAND' ? 'expertReviewDealLand' : 'expertReviewDealBuilding';

export const sparkBars = (history: Array<number>) => {
  const min = history.reduce((low, value) => Math.min(low, value), Infinity);
  const max = history.reduce((high, value) => Math.max(high, value), -Infinity);
  const span = max - min || 1;
  return history.map((value) => 14 + ((value - min) / span) * 86);
};

export const useExpertReviewI18n = () => {
  const { t, te, locale } = useI18n();
  return {
    translate: (key: string, params?: Record<string, unknown>) =>
      te(key) ? t(key, params || {}) : key,
    isArabic: computed(() => locale.value === 'ar'),
  };
};
